import { debounce } from "lodash";
import { useListFilterContext } from "ra-core";
import { FC, ReactElement, createContext, useContext, useMemo, useState } from "react";
import { PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { FileTagResponseDto, SearchInput } from "@/gen/api/securefile";
import { SimpleOption, SimpleOption2 } from "../governance/search/AmbientFacilitiesContext";


interface SecureFilesTableSearchContextProps {
  states: {
    term: string;
    tags: FileTagResponseDto[];
    primaryClassifications: PrimaryClassification[];
    secondaryClassifications: SecondaryClassificationResponseDto[];
    startTime: Date | null;
    endTime: Date | null;
    user: SimpleOption | null;
    machine: SimpleOption2 | null;
  };
  searchName: (s: string) => void;
  setSearchTag: (tag: FileTagResponseDto, remove: boolean) => void;
  setSearchSClss: (s: SecondaryClassificationResponseDto, remove: boolean) => void;
  setSearchPClss: (p: PrimaryClassification, remove: boolean) => void;
  setSearchTime: (start: Date | null, end: Date | null) => void;
  setUser: (u: SimpleOption | null) => void;
  setMachine: (m: SimpleOption2 | null) => void;
}

export const SecureFilesTableSearchContext = createContext<SecureFilesTableSearchContextProps>({} as any)


export const SecureFilesTableSearchProvider : FC = ({ children }) => {
  const { setFilters, displayedFilters } = useListFilterContext()

  const [states, setStates] = useState({
    term: '',
    tags: [] as FileTagResponseDto[],
    primaryClassifications: [] as PrimaryClassification[],
    secondaryClassifications: [] as SecondaryClassificationResponseDto[],
    startTime: null as Date|null,
    endTime: null as Date|null,
    user: null as SimpleOption|null,
    machine: null as SimpleOption2|null,
  })

  const ctrls = useMemo(() => {
    const invokeSearch = (s1: typeof states) => {
      const input : SearchInput = {
        term: s1.term || null,
        tags: s1.tags.length > 0 ? s1.tags.map(t => t.tagId!) : null,
        primaryClassifications: s1.primaryClassifications.length > 0 ? s1.primaryClassifications : null,
        secondaryClassifications: s1.secondaryClassifications.length > 0 ? s1.secondaryClassifications.map(s => s.secondaryClassificationId!) : null,
        startTime: s1.startTime ? s1.startTime.toISOString() : null,
        endTime: s1.endTime ? s1.endTime.toISOString() : null,
        userId: s1.user ? s1.user.key : null,
        machine: s1.machine ? s1.machine.key : null,
      }
      setFilters(input, displayedFilters)
    }
  
    const debouncedInvokeSearch = debounce(invokeSearch, 300)
  
    return {
      searchName: (s: string) => {
        setStates(v => {
          if (v.term !== s) {
            const v1 = {...v, term: s}
            setTimeout(() => debouncedInvokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },  
      setSearchTag: (tag: FileTagResponseDto, remove: boolean) => {
        setStates(v => {
          const i = v.tags.findIndex(t => t.tagId === tag.tagId)
          if (remove && i > -1) {
            const tags = [...v.tags]
            tags.splice(i, 1)
            const v1 = {...v, tags }
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.tags.push(tag)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },  
      setSearchSClss: (s: SecondaryClassificationResponseDto, remove: boolean) => {
        setStates(v => {
          const i = v.secondaryClassifications.findIndex(x => x.secondaryClassificationId === s.secondaryClassificationId)
          if (remove && i > -1) {
            const secondaryClassifications = [...v.secondaryClassifications]
            secondaryClassifications.splice(i, 1)
            const v1 = {...v, secondaryClassifications }
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.secondaryClassifications.push(s)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },  
      setSearchTime: (start:Date|null, end:Date|null) => {
        setStates(v => {
          if (v.startTime === start && v.endTime === end) {
            return v
          } else {
            const v1 = {...v, startTime: start, endTime: end}
            setTimeout(() => invokeSearch(v1))
            return v1
          }
        })
      },  
      setSearchPClss: (p: PrimaryClassification, remove: boolean) => {
        setStates(v => {
          const i = v.primaryClassifications.findIndex(x => x == p)
          if (remove && i > -1) {
            const primaryClassifications = [...v.primaryClassifications]
            primaryClassifications.splice(i, 1)
            const v1 = {...v, primaryClassifications }
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.primaryClassifications.push(p)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },
      setUser: (u: SimpleOption|null) => {
        setStates(v => {
          if (u == null && v.user == null) { return v }
          if (u?.key === v.user?.key) { return v }
          const v1 = {...v, user: u}
          setTimeout(() => invokeSearch(v1))
          return v1
        })
      },
      setMachine: (m: SimpleOption2|null) => {
        setStates(v => {
          if (!m && !v.machine) { return v }
          if (v.machine?.key === m?.key) { return v }
          const v1 = {...v, machine: m }
          setTimeout(() => invokeSearch(v1))
          return v1
        })
      }
    }
  }, [setFilters, displayedFilters])

  return (
    <SecureFilesTableSearchContext.Provider value={{...ctrls, states}}>
      {children}
    </SecureFilesTableSearchContext.Provider>
  )
}

export const useSecureFilesTableSearchContext = () => useContext(SecureFilesTableSearchContext)

export const WithSFTSC : FC<{ render: (s: SecureFilesTableSearchContextProps['states']) => ReactElement|null }> = ({ render }) => {
  const { states } = useSecureFilesTableSearchContext()
  return render(states) ?? <></>
}
